/* eslint-disable no-undef */
/**
 * Defines functions for managing windows.  It attempts to hide differences between browser windows and
 * CEF windows.
 *
 * @module MW/uiframework/uicontainer/WindowUtils
 * @copyright Copyright 2017-2020 The MathWorks, Inc.
 */
define(['dojo/on', // eslint-disable-next-line indent
        'mw-utils/Utils'],
function (on, Utils) {
    var defaultXPosition = 0.1;
    var defaultYPosition = 0.1;

    // Window specific maps
    var windowBoundsListeners = {};
    var windowResizedHandlers = {};
    var handleWindowResizedOverridden = false;

    var isObject = function (obj) {
        return obj === Object(obj);
    };

    var hasProperties = function (obj) {
        // Unlike 'Object.hasOwnProperty()', 'for...in'
        //     1. Works with 'Object.create(null)' maps
        //     2. Will search up the prototype chain
        if (isObject(obj)) {
            for (let prop in obj) {
                return true;
            }
        }
        return false;
    };

    var WindowUtils = {

        applyDefaultBounds: function (bounds, singleDocumentWindow) {
            if (bounds.x === undefined) {
                bounds.x = defaultXPosition;
                defaultXPosition += 0.015;
                if (defaultXPosition > 0.5) {
                    defaultXPosition = 0.1;
                }
            }
            if (bounds.y === undefined) {
                bounds.y = defaultYPosition;
                defaultYPosition += 0.02;
                if (defaultYPosition > 0.5) {
                    defaultYPosition = 0.1;
                }
            }
            if ((bounds.w === undefined) || (bounds.w < 0)) {
                bounds.w = singleDocumentWindow ? 0.667 : 0.8;
            }
            if ((bounds.h === undefined) || (bounds.h < 0)) {
                bounds.h = 0.8;
            }
        },

        convertBoundsToPixels: function (bounds) {
            if (bounds.x > 0 && bounds.x < 1) {
                bounds.x = Math.floor(bounds.x * window.screen.width);
            }
            if (bounds.y > 0 && bounds.y < 1) {
                bounds.y = Math.floor(bounds.y * window.screen.height);
            }
            if (bounds.w > 0 && bounds.w < 1) {
                bounds.w = Math.floor(bounds.w * window.screen.width);
            }
            if (bounds.h > 0 && bounds.h < 1) {
                bounds.h = Math.floor(bounds.h * window.screen.height);
            }
        },

        /**
         * Tracks bounds changes for browser or CEF windows.
         *
         * @param targetWindow
         * @param callback - called when window bounds changed.  Passed the updatedBounds and a moreExpected flag.
         *                   The latter is a boolean the will be true if the update is part of a sequence that is
         *                   expected to continue.
         * @param origin   - Determines how addWindowBoundsListener reports the bounds. If set to WindowUtils.BOTTOM_LEFT_ORIGIN
         *                   then bounds will be reported from a bottom left reference point.
         */
        addWindowBoundsListener: function (targetWindow, callback, origin) {
            var uuid = Utils.generateUuid();

            if (targetWindow.cefclient) {
                if (!handleWindowResizedOverridden) {
                    if (targetWindow.cefclient.handleWindowResized) {
                        windowResizedHandlers.originalwindowResizedHandler = targetWindow.cefclient.handleWindowResized;
                    }

                    targetWindow.cefclient.handleWindowResized = function (positionString) {
                        if (hasProperties(windowResizedHandlers)) {
                            for (let handlerId in windowResizedHandlers) {
                                windowResizedHandlers[handlerId](positionString);
                            }
                        }
                    };

                    handleWindowResizedOverridden = true;
                }

                windowResizedHandlers[uuid] = (positionString) => {
                    var positionArray = positionString.split(',');
                    var updatedBounds = {
                        x: parseInt(positionArray[0]),
                        y: parseInt(positionArray[1]),
                        w: parseInt(positionArray[2]),
                        h: parseInt(positionArray[3])
                    };
                    if ((origin === undefined) || (origin === WindowUtils.TOP_LEFT_ORIGIN)) {
                        updatedBounds = this.convertOrigin(targetWindow, updatedBounds);
                    }
                    // I haven't found it documented anywhere but the position string appears to contain a last
                    // value that is 1 if the window size has stopped changing and 0 otherwise.
                    var more = positionArray.length >= 5 && parseInt(positionArray[4]) > 0;
                    callback(updatedBounds, more);
                };

                windowBoundsListeners[uuid] = {
                    windowResizedHandler: windowResizedHandlers[uuid]
                };
            } else {
                var targetWindowResizeListener;
                var targetWindowLoadListener;
                var targetWindowUnloadListener;

                // Listen for resize
                targetWindowResizeListener = on(targetWindow, 'resize', () => {
                    var bounds = this.getWindowBounds(targetWindow);
                    if (origin === WindowUtils.BOTTOM_LEFT_ORIGIN) {
                        bounds = this.convertOrigin(targetWindow, bounds);
                    }
                    callback(bounds, false);
                });

                // Poll for move since there is no associated event available
                var lastBounds = this.getWindowBounds(targetWindow);
                var interval = setInterval(() => {
                    // If the target window is closed immediately after being opened the unload listener
                    // (see below) never fires.  If it is found to have been closed, stop the polling.
                    if (targetWindow.closed) {
                        clearInterval(interval);
                        return;
                    }
                    var updatedBounds = this.getWindowBounds(targetWindow);
                    if (updatedBounds.x !== lastBounds.x || updatedBounds.y !== lastBounds.y) {
                        lastBounds.x = updatedBounds.x;
                        lastBounds.y = updatedBounds.y;

                        if (origin === WindowUtils.BOTTOM_LEFT_ORIGIN) {
                            updatedBounds = this.convertOrigin(targetWindow, updatedBounds);
                        }
                        callback(updatedBounds, false);
                    }
                }, 250);
                // For some reason the unload event fires immediately after a child window is opened.
                // Avoid a false unload by waiting for load before starting to listen for unload.
                targetWindowLoadListener = on(targetWindow, 'load', () => {
                    targetWindowUnloadListener = on(targetWindow, 'unload', () => {
                        clearInterval(interval);
                        if (targetWindowUnloadListener && targetWindowUnloadListener.remove) {
                            targetWindowUnloadListener.remove();
                        }
                    });
                });

                windowBoundsListeners[uuid] = {
                    onListeners: [
                        targetWindowResizeListener,
                        targetWindowLoadListener
                    ],
                    intervalIds: [interval]
                };
            }

            return uuid;
        },

        removeWindowBoundsListener: function (uuid) {
            if (uuid in windowBoundsListeners) {
                const listeners = windowBoundsListeners[uuid];

                // clear intervals
                if (listeners.intervalIds && listeners.intervalIds.length > 0) {
                    listeners.intervalIds.forEach((interval) => {
                        clearInterval(interval);
                    });
                }

                // remove 'on' listeners
                if (listeners.onListeners && listeners.onListeners.length > 0) {
                    listeners.onListeners.forEach((listener) => {
                        if (listener && listener.remove) {
                            listener.remove();
                        }
                    });
                }

                // remove 'windowResized' handler
                if (listeners.windowResizedHandler) {
                    delete windowResizedHandlers[uuid];
                }

                // Cleanup 'windowBoundsListeners'
                delete windowBoundsListeners[uuid];
            }
        },

        /**
         * Sets the location of the window relative to the top-left of the screen
         *
         * @param targetWindow
         * @param origin - Determines how setWindowBounds interperets the input bounds. If set to WindowUtils.BOTTOM_LEFT_ORIGIN
         *                 then bounds parameter will be treated as if the values are from a bottom left reference point.
         */
        setWindowBounds: function (targetWindow, bounds, origin) {
            if ((targetWindow.cefclient && (origin !== WindowUtils.BOTTOM_LEFT_ORIGIN)) || (!targetWindow.cefclient && (origin === WindowUtils.BOTTOM_LEFT_ORIGIN))) {
                bounds = this.convertOrigin(targetWindow, bounds);
            }

            if (targetWindow.cefclient) {
                targetWindow.cefclient.sendMessage('setPosition', [bounds.x, bounds.y, bounds.w, bounds.h]);
            } else {
                // Resize window to be very small first, so that we have the best chance of
                // accurately getting the initial moveTo done correctly. Otherwise, if the
                // window's existing width/height is large, then moveTo might not be
                // accurate, as the browser will not move a window to a point where part of
                // it dangles off the screen
                targetWindow.resizeTo(1, 1); // Does not work on Firefox.
                targetWindow.moveTo(bounds.x, bounds.y);
                targetWindow.resizeTo(bounds.w, bounds.h);
            }
        },

        /**
         * Returns the location of the window relative to the top-left of the screen
         *
         * @param targetWindow
         * @param origin - Determines how getWindowBounds will return the bounds. If set to WindowUtils.BOTTOM_LEFT_ORIGIN
         *                 then bounds will be returned from a bottom left reference point.
         */
        getWindowBounds: function (targetWindow, origin) {
            var bounds = {
                x: (typeof targetWindow.screenX) === 'number'
                    ? targetWindow.screenX
                    : ((typeof targetWindow.screenLeft) === 'number' ? targetWindow.screenLeft : 0),
                y: (typeof targetWindow.screenY) === 'number'
                    ? targetWindow.screenY
                    : ((typeof targetWindow.screenTop) === 'number' ? targetWindow.screenTop : 0),
                w: targetWindow.outerWidth,
                h: targetWindow.outerHeight
            };

            if (origin === WindowUtils.BOTTOM_LEFT_ORIGIN) {
                bounds = this.convertOrigin(targetWindow, bounds);
            }

            return bounds;
        },

        convertOrigin: function (targetWindow, bounds) {
            let outBounds = Object.assign({}, bounds);
            outBounds.y = targetWindow.screen.height - outBounds.y - outBounds.h;
            return outBounds;
        },

        bringToFront: function (targetWindow) {
            if (targetWindow.cefclient) {
                targetWindow.cefclient.sendMessage('bringToFront');
            } else {
                targetWindow.focus();
            }
        }
    };

    // Constant name definitions
    WindowUtils.BOTTOM_LEFT_ORIGIN = 'bottomLeftOrigin';
    WindowUtils.TOP_LEFT_ORIGIN = 'topLeftOrigin';

    return WindowUtils;
});
